import React from 'react';

/**
 * AIAgentsClip
 * Looping SVG clip: a central agent node "reasons" over the Context Model.
 *  - Context particles flow inward from data sources
 *  - Agent emits decisions (outbound arrows) to downstream actions
 *  - Pulsing rings + tool calls indicator
 */
const AIAgentsClip = () => {
  return (
    <div className="relative w-full" data-testid="ai-agents-clip">
      <style>{`
        @keyframes aia-ring {
          0%   { r: 38; opacity: 0.55; }
          80%  { r: 70; opacity: 0; }
          100% { r: 70; opacity: 0; }
        }
        @keyframes aia-in-1 { 0%{offset-distance:0%;opacity:0} 10%{opacity:1} 95%{offset-distance:100%;opacity:1} 100%{opacity:0} }
        @keyframes aia-in-2 { 0%,15%{offset-distance:0%;opacity:0} 25%{opacity:1} 100%{offset-distance:100%;opacity:1} }
        @keyframes aia-in-3 { 0%,30%{offset-distance:0%;opacity:0} 40%{opacity:1} 100%{offset-distance:100%;opacity:1} }
        @keyframes aia-out-1{ 0%,50%{offset-distance:0%;opacity:0} 55%{opacity:1} 100%{offset-distance:100%;opacity:1} }
        @keyframes aia-out-2{ 0%,65%{offset-distance:0%;opacity:0} 70%{opacity:1} 100%{offset-distance:100%;opacity:1} }
        @keyframes aia-think {
          0%, 100% { opacity: 0.55; }
          50%      { opacity: 1; }
        }
        @keyframes aia-token {
          0%, 100% { width: 0; }
          40%      { width: 36px; }
          70%      { width: 64px; }
        }
        @keyframes aia-scan {
          0%   { transform: translateY(-100%); opacity: 0; }
          15%  { opacity: 0.55; }
          85%  { opacity: 0.55; }
          100% { transform: translateY(100%); opacity: 0; }
        }
        .aia-particle { will-change: offset-distance, opacity; }
        .aia-ring { animation: aia-ring 3.6s ease-out infinite; transform-origin: center; }
        .aia-ring-2 { animation-delay: 1.2s; }
        .aia-ring-3 { animation-delay: 2.4s; }
        .aia-think { animation: aia-think 1.6s ease-in-out infinite; }
        .aia-scan  { animation: aia-scan 5s ease-in-out infinite; }
      `}</style>

      <div className="relative rounded-2xl border border-neutral-800 bg-gradient-to-br from-neutral-950 via-black to-neutral-950 p-5 overflow-hidden">
        <div className="flex items-center justify-between text-[11px]">
          <div className="flex items-center gap-2 text-neutral-400">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 aia-think" />
            <span className="uppercase tracking-[0.18em]">Reasoning agent · context aware</span>
          </div>
          <div className="flex gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
            <span className="w-1.5 h-1.5 rounded-full bg-neutral-700" />
          </div>
        </div>

        <div aria-hidden className="absolute inset-0 opacity-[0.18] pointer-events-none" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
          maskImage: 'radial-gradient(ellipse at center, black 50%, transparent 90%)',
        }} />
        <div aria-hidden className="absolute inset-x-0 top-0 h-full pointer-events-none">
          <div className="aia-scan absolute inset-x-0 h-24" style={{
            background: 'linear-gradient(to bottom, transparent, rgba(59,130,246,0.18), transparent)',
            filter: 'blur(6px)',
          }} />
        </div>

        <svg viewBox="0 0 460 230" className="relative w-full h-auto mt-3" role="img" aria-label="AI agent reasoning animation">
          <defs>
            <radialGradient id="aia-core" cx="50%" cy="50%" r="50%">
              <stop offset="0%"  stopColor="#1e40af" />
              <stop offset="100%" stopColor="#0b1220" />
            </radialGradient>
            <filter id="aia-glow"><feGaussianBlur stdDeviation="2.4" /></filter>
          </defs>

          {/* Source nodes (left) */}
          {[
            { y: 50,  label: 'Process graph' },
            { y: 110, label: 'KPIs' },
            { y: 170, label: 'History' },
          ].map((s, i) => (
            <g key={s.label}>
              <rect x="20" y={s.y - 14} width="100" height="28" rx="8" fill="#0b1220" stroke="#1f2937" />
              <text x="70" y={s.y + 4} textAnchor="middle" fill="#cbd5e1" fontSize="10.5" fontFamily="ui-sans-serif, system-ui">{s.label}</text>
              <line x1="120" y1={s.y} x2="200" y2="115" stroke="#1e3a8a" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
            </g>
          ))}

          {/* Action nodes (right) */}
          {[
            { y: 70,  label: 'Trigger workflow' },
            { y: 150, label: 'Notify owner' },
          ].map((a) => (
            <g key={a.label}>
              <line x1="260" y1="115" x2="340" y2={a.y} stroke="#1e3a8a" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
              <rect x="340" y={a.y - 14} width="110" height="28" rx="8" fill="#0b1220" stroke="#1f2937" />
              <text x="395" y={a.y + 4} textAnchor="middle" fill="#cbd5e1" fontSize="10.5" fontFamily="ui-sans-serif, system-ui">{a.label}</text>
            </g>
          ))}

          {/* Pulsing rings */}
          <circle cx="230" cy="115" r="38" fill="none" stroke="#3b82f6" strokeWidth="1.4" className="aia-ring" />
          <circle cx="230" cy="115" r="38" fill="none" stroke="#3b82f6" strokeWidth="1.4" className="aia-ring aia-ring-2" />
          <circle cx="230" cy="115" r="38" fill="none" stroke="#3b82f6" strokeWidth="1.4" className="aia-ring aia-ring-3" />

          {/* Agent core */}
          <circle cx="230" cy="115" r="38" fill="url(#aia-core)" stroke="#3b82f6" strokeWidth="1.5" />
          <circle cx="230" cy="115" r="38" fill="none" stroke="#60a5fa" strokeOpacity="0.35" strokeWidth="6" filter="url(#aia-glow)" />
          <text x="230" y="111" textAnchor="middle" fill="#dbeafe" fontSize="11" fontFamily="ui-sans-serif, system-ui" fontWeight="600">Agent</text>
          <text x="230" y="125" textAnchor="middle" fill="#93c5fd" fontSize="9" fontFamily="ui-sans-serif, system-ui">reasoning…</text>

          {/* Inbound particles */}
          <circle r="3.5" fill="#60a5fa" className="aia-particle" style={{ offsetPath: "path('M 120 50 L 200 115')", animation: 'aia-in-1 3.2s linear infinite' }} />
          <circle r="3.5" fill="#60a5fa" className="aia-particle" style={{ offsetPath: "path('M 120 110 L 200 115')", animation: 'aia-in-2 3.2s linear infinite' }} />
          <circle r="3.5" fill="#60a5fa" className="aia-particle" style={{ offsetPath: "path('M 120 170 L 200 115')", animation: 'aia-in-3 3.2s linear infinite' }} />

          {/* Outbound decisions */}
          <circle r="3.5" fill="#34d399" className="aia-particle" style={{ offsetPath: "path('M 260 115 L 340 70')",  animation: 'aia-out-1 3.2s linear infinite' }} />
          <circle r="3.5" fill="#34d399" className="aia-particle" style={{ offsetPath: "path('M 260 115 L 340 150')", animation: 'aia-out-2 3.2s linear infinite' }} />
        </svg>

        <div className="mt-1 flex items-center justify-between text-[10.5px] text-neutral-500">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-blue-400" /> Context in</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Decision out</span>
          </div>
          <span className="tabular-nums">tool calls · 4</span>
        </div>
      </div>
    </div>
  );
};

export default AIAgentsClip;
